import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "./Logo";
import apiClient from "../API/apiClient";
import "../../styles/layout/sidebar.css";
import { usePermissions } from "../../context/PermissionContext";

const Sidebar = ({ isOpen, setIsOpen }) => {
  const [menus, setMenus] = useState([]);
  const { setPermissions } = usePermissions();
  
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  const contentRef = useRef(null);
  const thumbRef = useRef(null);

  const closeSidebar = () => setIsOpen(false);

  const decodeHTML = (html) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  };

  const renderIcon = (icon) => {
    if (!icon) return null;

    if (icon.trim().startsWith("<")) {
      return (
        <div
          className="nav-icon"
          dangerouslySetInnerHTML={{ __html: decodeHTML(icon) }}
        />
      );
    }

    return <i className={`nav-icon ${icon}`} />;
  };

  useEffect(() => {
    loadMenus();
  }, []);

  useEffect(() => {
    const content = contentRef.current;
    const thumb = thumbRef.current;

    const handleScroll = () => {
      if (!content || !thumb) return;

      const { scrollTop, scrollHeight, clientHeight } = content;

      const scrollRatio = scrollTop / (scrollHeight - clientHeight || 1);

      const thumbHeight = (clientHeight / scrollHeight) * clientHeight;

      const thumbTop = scrollRatio * (clientHeight - thumbHeight);

      thumb.style.height = `${thumbHeight}px`;
      thumb.style.top = `${thumbTop}px`;
    };

    content?.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => content?.removeEventListener("scroll", handleScroll);
  }, []);

  // ✅ Close sidebar on route change
  useEffect(() => {
    closeSidebar();
  }, [location.pathname]);

  // ✅ Prevent body scroll when sidebar open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "auto";
  }, [isOpen]);

  const loadMenus = async () => {
  try {

    const res = await apiClient.get("/api/modules");

    setMenus(res.data.data || []);

    setPermissions(res.data.permissions || {});

  } catch (error) {

    console.error("Menu API Error:", error);

  } finally {

    setLoading(false);

  }
};

const isActive=(path)=>{

  if(!path) return false;

  const currentPath=
    location.pathname.toLowerCase();

  const normalizedPath=
    path.startsWith("/dashboard")
      ? path.toLowerCase()
      : `/dashboard/${path.replace(/^\/+/,"")}`.toLowerCase();

  // exact dashboard only
  if(normalizedPath==="/dashboard"){

    return currentPath==="/dashboard";
  }

  // other menus
  return (
    currentPath===normalizedPath ||
    currentPath.startsWith(`${normalizedPath}/`)
  );
};

  const shouldShowParent = (menu) => {
    if (!menu.submenus || menu.submenus.length === 0) return menu.is_enabled;

    return menu.submenus.some((sub) => sub.is_enabled);
  };

  return (
    <>
      {/* ✅ Overlay */}
      {isOpen && <div className="sidebar-overlay" onClick={closeSidebar}></div>}

      {/* ✅ Sidebar */}
      <div className={`sidebar-container ${isOpen ? "open" : ""}`}>
        <div className="sidebar-content" ref={contentRef}>
          <aside className="sidebar">
            {/* Logo */}
            <div className="sidebar-logo">
              <Logo showMargin={false} />
            </div>

            {/* Nav */}
            <nav className="sidebar-nav">
              {loading && (
                <div className="dash-loader-wrapper">
                  <div className="dash-loader"></div>
                  <p className="dash-loader-text">Loading Menu</p>
                </div>
              )}

              {!loading &&
                menus.map((menu) => {
                  if (!shouldShowParent(menu)) return null;

                  const hasSubmenus = menu.submenus && menu.submenus.length > 0;

                  // ✅ Leaf
                  if (!hasSubmenus) {
                    return (
                      <div
                        key={menu.id}
                        className={`nav-item ${
                          isActive(menu.path) ? "active" : ""
                        }`}
                        onClick={() => {
                          navigate(menu.path);
                          closeSidebar();
                        }}
                      >
                        {renderIcon(menu.icon)}
                        <span className="nav-text">{menu.name}</span>
                      </div>
                    );
                  }

                  // ✅ Parent
                  return (
                    <div key={menu.id}>
                      <div className="nav-section-label">{menu.name}</div>

                      {menu.submenus.map((sub) => {
                        if (!sub.is_enabled) return null;

                        return (
                          <div
                            key={sub.id}
                            className={`nav-item ${
                              isActive(sub.path) ? "active" : ""
                            }`}
                            onClick={() => {
                              navigate(sub.path);
                              closeSidebar();
                            }}
                          >
                            {renderIcon(sub.icon)}
                            <span className="nav-text">{sub.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
            </nav>
          </aside>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
